import { supabase } from "@/integrations/supabase/client";

/**
 * V7.1 — live tenant entitlement freshness.
 *
 * Root cause of the observed gap: `public.tenant_modules` is NOT part of the
 * `supabase_realtime` publication and the entitlement query used
 * `staleTime: 5 min` with `refetchOnWindowFocus: false`. An already logged-in
 * session therefore kept a cached entitlement snapshot until a full reload.
 *
 * This module provides two bounded, fail-open freshness mechanisms:
 *  1. A shared (ref-counted) realtime channel per tenant. If the table is later
 *     added to the publication, propagation becomes near-instant with zero
 *     further code change. Until then the channel is inert and harmless.
 *  2. A conservative focus / visibility / reconnect refetch fallback driven by
 *     React Query options in `useTenantCapabilities`.
 *
 * No polling loop is introduced; nothing here can block access — every failure
 * path leaves the existing cached (fail-open) verdict untouched.
 */

export interface TenantModulesChangePayload {
  eventType?: string;
  new?: { tenant_id?: string | null } | null;
  old?: { tenant_id?: string | null } | null;
}

/** Pure: does this realtime payload concern the given tenant? */
export function isTenantModulesChangeForTenant(
  payload: TenantModulesChangePayload | null | undefined,
  tenantId: string | null | undefined,
): boolean {
  if (!payload || !tenantId) return false;
  const next = payload.new?.tenant_id ?? null;
  const prev = payload.old?.tenant_id ?? null;
  return next === tenantId || prev === tenantId;
}

/** Query keys that must be invalidated when entitlements change. */
export function tenantModulesQueryKeys(tenantId: string) {
  return [
    ["tenant-modules", tenantId],
    ["super-admin-tenant-modules", tenantId],
  ];
}

type Listener = () => void;

interface Entry {
  channel: ReturnType<typeof supabase.channel>;
  listeners: Set<Listener>;
}

const registry = new Map<string, Entry>();

/**
 * Ref-counted subscription. Multiple hook instances for the same tenant share a
 * single Supabase channel; the channel is removed once the last listener leaves
 * (logout, tenant switch, unmount) so no channels or memory leak.
 */
export function subscribeTenantModules(tenantId: string, listener: Listener): () => void {
  let entry = registry.get(tenantId);

  if (!entry) {
    const channel = supabase
      .channel(`tenant-modules:${tenantId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "tenant_modules",
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload: TenantModulesChangePayload) => {
          if (!isTenantModulesChangeForTenant(payload, tenantId)) return;
          const current = registry.get(tenantId);
          current?.listeners.forEach((fn) => {
            try {
              fn();
            } catch {
              /* a failing consumer must never break the others */
            }
          });
        },
      );

    entry = { channel, listeners: new Set() };
    registry.set(tenantId, entry);
    channel.subscribe();
  }

  entry.listeners.add(listener);

  return () => {
    const current = registry.get(tenantId);
    if (!current) return;
    current.listeners.delete(listener);
    if (current.listeners.size === 0) {
      registry.delete(tenantId);
      void supabase.removeChannel(current.channel);
    }
  };
}

/** Test/introspection helper. */
export function activeTenantModuleChannelCount(): number {
  return registry.size;
}
